import { ComponentProps, useMemo } from 'react'
import { SceneLineMarker } from '@scene'
import useSceneLineMarker from './useSceneLineMarker'

type SceneShapeMarkerPoints = ComponentProps<typeof SceneLineMarker>['lines'][number]

/**
 * Adds a temporary closed shape outline to the scene.
 * Useful for highlighting a storey, slab, or other planar boundary without mutating the model.
 */
const useSceneShapeMarker = ({
  points,
  enabled,
  color,
  lineWidth,
  dashed,
  dashSize,
  gapSize,
  dashScale,
  renderOrder,
}: {
  points: SceneShapeMarkerPoints
  enabled: boolean
  color?: string
  lineWidth?: number
  dashed?: boolean
  dashSize?: number
  gapSize?: number
  dashScale?: number
  renderOrder?: number
}) => {
  const lines = useMemo<ComponentProps<typeof SceneLineMarker>['lines']>(() => {
    if (points.length < 2) return []
    if (points.length === 2) return [points]

    const firstPoint = points[0]
    const lastPoint = points[points.length - 1]
    const isClosed =
      firstPoint.x === lastPoint.x && firstPoint.y === lastPoint.y && firstPoint.z === lastPoint.z

    return [isClosed ? points : [...points, firstPoint]]
  }, [points])

  useSceneLineMarker({
    lines,
    enabled: enabled && lines.length > 0,
    color,
    lineWidth,
    dashed,
    dashSize,
    gapSize,
    dashScale,
    renderOrder,
  })
}

export default useSceneShapeMarker
